import { useState, useRef, useEffect } from 'react';
import { Play, Pause } from 'lucide-react';

function fmt(s) {
  if (!s || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, '0');
  return `${m}:${sec}`;
}

/**
 * VoiceMessage — playback bubble for an audio_url.
 * Lightweight, no external waveform lib — uses a 14-bar generated pseudo-waveform
 * (seeded by url length) for a polished look without per-message DSP cost.
 */
export default function VoiceMessage({ url, duration, isMine }) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [actualDur, setActualDur] = useState(duration || 0);
  const audioRef = useRef(null);

  useEffect(() => {
    const a = new Audio(url);
    audioRef.current = a;
    const onTime = () => setProgress(a.currentTime);
    const onLoaded = () => { if (a.duration && isFinite(a.duration)) setActualDur(a.duration); };
    const onEnd = () => { setPlaying(false); setProgress(0); };
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onLoaded);
    a.addEventListener('ended', onEnd);
    return () => {
      a.pause();
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onLoaded);
      a.removeEventListener('ended', onEnd);
    };
  }, [url]);

  const toggle = (e) => {
    e.stopPropagation();
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play().then(() => setPlaying(true)).catch(() => {}); }
  };

  // Generate a stable pseudo-waveform from url string
  const bars = [];
  const seed = (url || '').length;
  for (let i = 0; i < 22; i++) {
    const h = 30 + ((seed * (i + 1) * 31) % 70);
    bars.push(h);
  }
  const playedRatio = actualDur > 0 ? progress / actualDur : 0;

  const barOn = isMine ? 'bg-[#3F5200]' : 'bg-[#6B8A00]';
  const barOff = isMine ? 'bg-[#3F5200]/30' : 'bg-[#9B9EA3]/30';

  return (
    <div className="flex items-center gap-2.5 min-w-[180px] py-0.5" data-testid="voice-message">
      <button
        type="button"
        onClick={toggle}
        data-testid="voice-play"
        className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isMine ? 'bg-[#3F5200]/15 text-[#3F5200]' : 'bg-[#F1F0EA] text-[#111111]'}`}
        aria-label={playing ? 'Pause voice note' : 'Play voice note'}
      >
        {playing ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" />}
      </button>
      <div className="flex items-center gap-[2px] flex-1 h-7">
        {bars.map((h, i) => {
          const on = (i + 1) / bars.length <= playedRatio;
          return (
            <span
              key={i}
              className={`w-[3px] rounded-full transition-colors ${on ? barOn : barOff}`}
              style={{ height: `${(h / 100) * 22 + 4}px` }}
            />
          );
        })}
      </div>
      <span className={`text-[10px] tabular-nums ${isMine ? 'text-[#3F5200]/70' : 'text-[#9B9EA3]'}`}>
        {fmt(playing ? progress : actualDur)}
      </span>
    </div>
  );
}
